import { RefObject, useEffect, useRef, useState } from "react"

type Settings = {
  /**
   * If you don't specify a width, the chart will take the full width of the parent component
   */
  width?: number
  /**
   * If you don't specify a height, the chart will calculated the necessary height based on the data
   */
  height: number
  marginTop?: number
  marginLeft?: number
  marginRight?: number
  marginBottom?: number
}

export type Dimensions = {
  width: number
  height: number
  marginTop: number
  marginRight: number
  marginBottom: number
  marginLeft: number
  boundedHeight: number
  boundedWidth: number
}

export const useChartResizer = (
  defaultSettings: Settings,
): [RefObject<HTMLDivElement>, Dimensions] => {
  const ref = useRef<HTMLDivElement>(null)
  const dimensions = combineChartDimensions(defaultSettings)
  const [localDimensions, setLocalDimensions] = useState({
    width: defaultSettings.width,
    height: defaultSettings.height,
  })

  useEffect(() => {
    if (dimensions.width && dimensions.height) return
    const element = ref.current
    const resizeObserver = new ResizeObserver(entries => {
      if (!Array.isArray(entries)) return
      if (!entries.length) return
      for (const entry of entries) {
        if (
          localDimensions.width !== entry.contentRect.width ||
          localDimensions.height !== entry.contentRect.height
        ) {
          setLocalDimensions({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          })
        }
      }
    })

    resizeObserver.observe(element as HTMLDivElement)

    return () => {
      resizeObserver.unobserve(element as HTMLDivElement)
    }
  }, [dimensions.height, localDimensions, dimensions.width])

  const newSettings = combineChartDimensions({
    ...dimensions,
    width: dimensions.width || localDimensions.width,
    height: dimensions.height || localDimensions.height,
  })
  return [ref, newSettings]
}
const combineChartDimensions = (settings: Settings): Dimensions => {
  const parsedDimensions = {
    ...settings,
    width: settings.width || 0,
    marginTop: settings.marginTop || 10,
    marginRight: settings.marginRight || 10,
    marginBottom: settings.marginBottom || 40,
    marginLeft: settings.marginLeft || 75,
  }

  return {
    ...parsedDimensions,
    boundedHeight: Math.max(
      parsedDimensions.height - parsedDimensions.marginTop - parsedDimensions.marginBottom,
      0,
    ),
    boundedWidth: Math.max(
      parsedDimensions.width - parsedDimensions.marginLeft - parsedDimensions.marginRight,
      0,
    ),
  }
}
